import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { COMPANY_INFO } from "../data/siteData";
import CmsGallery from "../components/CmsGallery";
import ReviewSlider from "../components/ReviewSlider";

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Om NF-Polering | Professionel Vinduespudser på Vestsjælland</title>
        <meta
          name="description"
          content="Læs mere om NF-Polering. Din lokale vinduespudser i Slagelse og på Vestsjælland. Vi leverer høj kvalitet, faste gennemsigtige priser og 100% tilfredshedsgaranti."
        />
        <link rel="canonical" href="https://nf-polering.dk/om-os" />
        <meta property="og:title" content="Om NF-Polering | Professionel Vinduespudser på Vestsjælland" />
        <meta
          property="og:description"
          content="Læs historien bag NF-Polering og vores fokus på skinnende rene ruder, god kundeservice og ærlige priser på hele Vestsjælland."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/gemini-generated-image-cq52wpcq52wpcq52.png"
        />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-50 border-b border-slate-200 text-slate-900 pt-12 pb-16 lg:pt-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Col: Text & CTAs */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-950 tracking-tight uppercase leading-tight">
                Om NF-Polering
              </h1>

              <p className="text-lg sm:text-xl text-slate-700 leading-relaxed font-medium max-w-2xl">
                Lokal vinduespudsning og professionel fliserens på Vestsjælland. Grundlagt med en klar ambition om at levere håndværksmæssig stolthed, pålidelighed og faste, ærlige priser.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base transition-transform duration-200 hover:-translate-y-1 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                  <span>{COMPANY_INFO.phone}</span>
                </a>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-base transition-transform duration-200 hover:-translate-y-1 whitespace-nowrap"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Col: Clean Hero Image */}
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden bg-slate-200 shadow-md">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/vestsja-lland_01a677ee.png"
                  alt="Vestsjælland – NF-Polering dækker hele lokalområdet"
                  className="w-full h-96 sm:h-[460px] lg:h-[520px] object-cover object-[center_20%] rounded-none"
                  width="900"
                  height="600"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values / 4 Highlights */}
      <section className="py-12 sm:py-14 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-center">
            <div className="flex flex-col items-center p-2">
              <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-base leading-tight">
                Lokalt forankret
              </h3>
              <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                Base i Slagelse og dækker hele Vestsjælland med faste ruter.
              </p>
            </div>

            <div className="flex flex-col items-center p-2">
              <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-base leading-tight">
                100% Tilfredshedsgaranti
              </h3>
              <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                Finder du mod forventning en stribe, retter vi det straks.
              </p>
            </div>

            <div className="flex flex-col items-center p-2">
              <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                <i className="fa-solid fa-tag" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-base leading-tight">
                Faste, gennemsigtige priser
              </h3>
              <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                Ingen ubehagelige overraskelser. Du kender prisen på forhånd.
              </p>
            </div>

            <div className="flex flex-col items-center p-2">
              <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                <i className="fa-solid fa-clock" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-base leading-tight">
                Hurtig respons & tid
              </h3>
              <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                Vi svarer hurtigt og tilpasser arbejdet til din hverdag.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Sections with alternating image/text */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {/* Row 1: Image Left, Text Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-2017.jpg"
                alt="NF-Polering vinduespolering"
                className="w-full h-80 sm:h-96 object-cover rounded-none shadow-sm"
              />
            </div>

            <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 uppercase tracking-tight">
                Hvem er NF-Polering?
              </h2>

              <p>
                NF-Polering drives af Nicklas, som har mange års erfaring med professionel vinduespolering og overfladerens. Vi servicerer i dag både private villaer, rækkehuse, sommerhuse, boligforeninger og erhvervsejendomme på hele Vestsjælland.
              </p>

              <p>
                Hos os er en aftale en aftale. Vi møder op til tiden, udfører arbejdet med stor grundighed og efterlader altid dine vindueskarme og udendørsarealer pæne og ordentlige. Vi benytter både traditionelt håndværksmæssigt pudseudstyr og moderne rentvandsanlæg med teleskopstænger til vanskeligt tilgængelige ruder i højden.
              </p>

              <ul className="space-y-2.5 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Personlig kontakt og direkte dialog med den udførende vinduespudser</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>SMS-varsel dagen før ankomst, så du altid ved, hvornår vi kommer</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Fuld ansvarsforsikring og dækning på alt udført arbejde</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Skånsomme, miljøvenlige sæber uden skadelige kemikalier</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Row 2: Text Left, Image Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 uppercase tracking-tight">
                Vores værdier og arbejdsmetode
              </h2>

              <p>
                Vi tror på langvarige kunderelationer frem for hurtige engangsbesøg. Derfor vælger de fleste af vores kunder en fast abonnementsløsning med pudsning hver 4., 8. eller 12. uge, hvor ruderne altid fremstår skinnende rene året rundt.
              </p>

              <p>
                Udover traditionel vinduespudsning tilbyder vi professionel fliserens, algebehandling og imprægnering. Vi fjerner effektivt flisepest, mos og mørke belægninger, så dine udendørsarealer genvinder deres oprindelige udtryk og holder i mange år fremover.
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/vinduespudsning"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-transform duration-200 hover:-translate-y-1"
                >
                  <span>Læs om vinduespudsning</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <Link
                  to="/fliserens"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-sm transition-transform duration-200 hover:-translate-y-1"
                >
                  <span>Læs om fliserens</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1152.jpg"
                alt="Professionelt arbejde hos NF-Polering"
                className="w-full h-80 sm:h-96 object-cover object-top rounded-none shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Billedgalleri */}
      <CmsGallery />

      {/* Kundeanmeldelser */}
      <ReviewSlider />

      {/* Bottom CTA with Background Image */}
      <section className="relative py-16 sm:py-20 bg-slate-900 text-white text-center overflow-hidden">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/galleri-rene-vinduer-stue.png"
          alt="Baggrund"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
        />
        <div className="absolute inset-0 bg-slate-950/80 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">
            Klar til skinnende rene vinduer?
          </h2>

          <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto">
            Få et uforpligtende tilbud i dag eller ring direkte til os på {COMPANY_INFO.phone}. Vi står klar til at hjælpe dig på hele Vestsjælland.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <a
              href={COMPANY_INFO.phoneTel}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base transition-transform duration-200 hover:-translate-y-1 shadow-lg"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span>Ring: {COMPANY_INFO.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white text-white hover:bg-white hover:text-slate-950 font-bold text-base transition-transform duration-200 hover:-translate-y-1"
            >
              <span>Udfyld tilbudsformular</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
